/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable no-trailing-spaces */
/* eslint-disable quotes */

import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, Accordion, AccordionSummary, AccordionDetails,
    TableContainer, Table, TableBody, TableCell, TableRow, TableHead,
    Paper, Tooltip, CircularProgress, Chip, Divider, IconButton, Fade, Stack,
    Tabs, Tab, Badge, TextField, InputAdornment, Alert
} from '@mui/material';
import Layout from '../components/Layout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRole } from '../contexts/RoleContext';
import ApiService from '../services/ApiService';
import FulfillmentUpdatesDialog from '../components/FulfillmentUpdatesDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
    CheckCircle,
    PlayCircle,
    TaskAlt,
    Info,
    Schedule,
    Close,
    Description,
    LocationOn,
    AttachMoney,
    Business,
    CalendarToday,
    Person,
    Email,
    Phone,
    Layers,
    ViewModule,
    NetworkCheck,
    Security,
    PhoneDisabled,
    Router,
    AllInclusive,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';

// Helper component for tooltips in the cost breakdown table
const InfoTooltip = ({ description }) => (
    <Tooltip title={description} arrow placement="right">
        <HelpOutlineIcon
            fontSize="small"
            sx={{
                ml: 0.5,
                color: 'text.secondary',
                fontSize: '16px',
                cursor: 'help',
                "&:hover": { color: 'primary.main' }
            }}
        />
    </Tooltip>
);

const TrackRequests = () => {
    // Existing state variables
    const [requests, setRequests] = useState([]);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [deleteResultDialogOpen, setDeleteResultDialogOpen] = useState(false);
    const [deleteResultMessage, setDeleteResultMessage] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [fulfillmentDialogOpen, setFulfillmentDialogOpen] = useState(false);
    const [currentFulfillmentUpdates, setCurrentFulfillmentUpdates] = useState([]);
    const [selectedFulfillmentRequestId, setSelectedFulfillmentRequestId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentTab, setCurrentTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // New state variables for quote review functionality
    const [quoteReviewDialogOpen, setQuoteReviewDialogOpen] = useState(false);
    const [selectedQuoteRequest, setSelectedQuoteRequest] = useState(null);
    const [quoteData, setQuoteData] = useState(null);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const [resultMessage, setResultMessage] = useState('');
    const [resultSuccess, setResultSuccess] = useState(false);

    const userRole = useRole();
    const navigate = useNavigate();
    const location = useLocation();

    // Read URL params on mount and apply as initial filters (e.g. from WIP card click-through)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const statusParam = params.get('request_status');
        const typeParam = params.get('request_type');
        if (statusParam) {
            setSearchQuery(statusParam);
        }
        if (typeParam) {
            const typeToTab = {
                'circuit_ordering': 'circuit',
                'Bandwidth Upgrade/Downgrade': 'bandwidth',
                'AGN Managed-Firewall Decommissioning': 'firewall',
                'AGN Webex Decommissioning': 'webex',
                'AGN IPT Decommissioning': 'ipt',
            };
            const tab = typeToTab[typeParam];
            if (tab) setCurrentTab(tab);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cost component descriptions for tooltips
    const costComponentDescriptions = {
        sdWanCircuits: "SD-WAN underlay circuit cost covering the monthly costs from the WAN circuit service provider",
        sdWanManagement: "AzT SD-WAN management monthly recurring charges covering the costs for the license bandwidth",
        sdWanRouter: "AzT SD-WAN router monthly recurring charges",
        sdWanImacd: "SD-WAN underlay circuit costs covering the onetime costs from the WAN circuit service provider.",
        accentureEffort: "Implementation costs for works in scope of this ODM. The costs covers the efforts from the Integrator.",
        sdWanEffort: "AzT SD-WAN team efforts covering the costs for the efforts from the Allianz SD-WAN technical team.",
        odmTeamEffort: "ODM team effort covering the costs for ODM request processing and coordination."
    };

    // Material numbers by request type
    const materialNumbers = {
        webex: {
            primary: '910530',
            description: 'Webconf Individual Service (5PD)'
        },
        ipt: {
            primary: '904590',
            description: 'Collab Effort'
        },
        agnFirewall: {
            primary: '910246',
            description: 'Firewall Individual Services'
        },
        sdwan: {
            circuits: '908138',
            management: '908134',
            router: '908137',
            imacd: '908374',
            accenture: '905513',
            odmTeam: '905513'
        }
    };

    // Fetch all requests on component mount (existing code)
    // REPLACE the useEffect hook:
    // REPLACE ONLY the useEffect fetchAllRequests function:

    useEffect(() => {
    const fetchAllRequests = async () => {
        try {
            setLoading(true);
            console.log('🔍 Starting to fetch all requests...');
            const startTime = Date.now();

            const allRequests = await ApiService.getRequestsWithFallback(userRole);

            // Sort requests by date - newest first
            const sortedRequests = allRequests.sort((a, b) => {
                // Try multiple date fields (different sources may use different field names)
                const dateA = new Date(
                    a.submission_date ||
                    a.submissionDate ||
                    a.created_at ||
                    a.createdAt ||
                    a.initiation_date ||
                    a.initiationDate ||
                    0
                );
                const dateB = new Date(
                    b.submission_date ||
                    b.submissionDate ||
                    b.created_at ||
                    b.createdAt ||
                    b.initiation_date ||
                    b.initiationDate ||
                    0
                );

                // Sort descending (newest first)
                return dateB - dateA;
            });

            setRequests(sortedRequests);

            const elapsed = Date.now() - startTime;
            console.log(`✅ All requests loaded and sorted in ${elapsed}ms, total: ${sortedRequests.length} items`);

        } catch (error) {
            console.error('❌ Error fetching requests:', error);
            setRequests([]);
            // You can add a toast notification here if needed
        } finally {
            setLoading(false);
        }
    };

    if (userRole && !userRole.loading) {
        fetchAllRequests();
    }
}, [userRole]);
    // Existing handler functions
    // REPLACE the handleView function:
    // REPLACE ONLY the handleView function:

    const handleView = async (request) => {
        try {
            console.log('👁️ === VIEW CLICKED ===');
            console.log('🎯 Request details:', {
                composite_id: request.id,
                original_id: request.original_id,
                source: request.source
            });

            let data;
            // 🔧 FIX: Use original_id for API calls
            if (request.source === 'agn_firewall_requests') {
                const endpoint = `/api/agn-firewall/${request.original_id}`;
                console.log('🟠 Fetching from AGN endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            } else if (request.source === 'webex_decommissioning_requests') {
                const endpoint = `/api/webex-decommissioning/${request.original_id}`;
                data = await ApiService.get(endpoint);
            } else if (request.source === 'ipt_decommissioning_requests') {
                const endpoint = `/api/ipt-decommissioning/${request.original_id}`;
                console.log('🟣 Fetching from IPT endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            } else if (request.source === 'circuit_order_requests') {
                const endpoint = `/api/circuit-orders/${request.original_id}`;
                console.log('🟢 Fetching from Circuit Orders endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            } else {
                const endpoint = `/api/requests/${request.original_id}`;
                console.log('🔵 Fetching from Bandwidth endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            }

            data.source = request.source;
            setSelectedRequest(data);
            setViewDialogOpen(true);
            console.log('✅ View data loaded successfully');
        } catch (error) {
            console.error('❌ Error fetching request details:', error);
        }
    };

    // REPLACE ONLY the handleUpdate function:
    // REPLACE the handleUpdate function:

    const handleUpdate = (request) => {
        console.log('✏️ === UPDATE CLICKED ===');
        console.log('🎯 Request details:', {
            composite_id: request.id,
            original_id: request.original_id,
            source: request.source,
            title: request.odm_title,
            request_state: request.request_state
        });

        // 🔧 FIX: Passing source to avoid ID collision between tables
        const sourceMap = {
            'requests': 'bandwidth',
            'agn_firewall_requests': 'agn_firewall',
            'webex_decommissioning_requests': 'agn_webex',
            'ipt_decommissioning_requests': 'agn_ipt',
            'circuit_order_requests': 'circuit_order'
        };

        const type = sourceMap[request.source];
        const url = type
            ? `/update-request/${request.original_id}?type=${type}`
            : `/update-request/${request.original_id}`;

        console.log(`🎯 Routing ${request.source} update through UpdateRequest.js:`, url);
        navigate(url);
    };


    // REPLACE ONLY the handleDelete function:

    const handleDelete = (request) => {
        console.log('🗑️ === DELETE CLICKED ===');
        console.log('🔍 Request Object:', {
            composite_id: request.id,
            original_id: request.original_id,
            source: request.source,
            title: request.odm_title
        });

        setSelectedRequestId(request.id); // Store composite ID
        setSelectedRequest(request); // 🔧 FIX: Store entire request object
        setDeleteDialogOpen(true);
    };

    const handleDownloadExcel = async (request) => {
        let blob = null;
        let downloadSource = 'Unknown';
        let defaultFileName = 'decommissioning.xlsx';

        try {
            // Determine which download method to use based on source
            if (request.source === 'ipt_decommissioning_requests') {
                // IPT requests
                try {
                    blob = await ApiService.downloadIPTExcel(request.original_id);
                    downloadSource = 'ODM Portal Database';
                    defaultFileName = 'ipt_decommissioning.xlsx';
                } catch (error) {
                    throw new Error('Failed to download IPT Excel file from ODM Portal');
                }
            } else {
                // Webex requests (existing logic)
                try {
                    blob = await ApiService.downloadWebexExcel(request.original_id);
                    downloadSource = 'ODM Portal Database';
                    defaultFileName = 'webex_decommissioning.xlsx';
                } catch (odmError) {
                    if (request.acai_request_id || request.acaiRequestId) {
                        console.log('📡 Attempting to download from Amelia ticket attachments...');
                        blob = await ApiService.downloadWebexExcelFromAmelia(request.original_id);
                        downloadSource = 'Amelia Ticket Attachments';
                        console.log('✅ Excel file downloaded from Amelia');
                    } else {
                        throw new Error('File not found in ODM Portal and no Amelia ticket associated with this request');
                    }
                }
            }

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = request.excel_file_name || defaultFileName;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            alert(`Excel file downloaded successfully from ${downloadSource}`);

        } catch (error) {
            alert(`Failed to download Excel file: ${error.message}\n\nTried sources:\n1. ODM Portal Database\n2. Amelia Ticket Attachments`);
        }
    };

    // REPLACE the confirmDelete function:
    // REPLACE ONLY the confirmDelete function:

    const confirmDelete = async () => {
        setDeleteDialogOpen(false);

        try {
            // 🔧 FIX: Use stored request object instead of array search
            const request = selectedRequest;

            if (!request || !request.original_id) {
                console.error('❌ No valid request selected for deletion');
                setDeleteResultMessage('Invalid request selected.');
                setDeleteSuccess(false);
                setDeleteResultDialogOpen(true);
                return;
            }

            console.log('🗑️ === CONFIRM DELETE ===');
            console.log('🎯 Using stored request:', {
                composite_id: request.id,
                original_id: request.original_id,
                source: request.source
            });

            let data;
            let endpoint;

            // 🔧 FIX: Route based on source + use original_id for API
            if (request.source === 'agn_firewall_requests') {
                endpoint = `/api/agn-firewall/${request.original_id}`;
                console.log('🟠 Using AGN endpoint:', endpoint);
                data = await ApiService.delete(endpoint);
            } else if (request.source === 'webex_decommissioning_requests') {
                endpoint = `/api/webex-decommissioning/${request.original_id}`;
                data = await ApiService.delete(endpoint);
            } else if (request.source === 'ipt_decommissioning_requests') {
                endpoint = `/api/ipt-decommissioning/${request.original_id}`;
                console.log('🟣 Using IPT endpoint:', endpoint);
                data = await ApiService.delete(endpoint);
            } else if (request.source === 'requests') {
                endpoint = `/api/requests/${request.original_id}`;
                console.log('🔵 Using Bandwidth endpoint:', endpoint);
                data = await ApiService.delete(endpoint);
            } else if (request.source === 'circuit_order_requests') {
                endpoint = `/api/circuit-orders/${request.original_id}`;
                console.log('🟢 Using Circuit Orders endpoint:', endpoint);
                data = await ApiService.delete(endpoint);
            } else {
                throw new Error(`❌ Unknown source: ${request.source}`);
            }

            console.log('✅ Delete response:', data);

            if (data.message) {
                setDeleteResultMessage('Request successfully deleted.');
                setDeleteSuccess(true);
                // 🔧 FIX: Remove using composite ID
                setRequests(requests.filter(req => req.id !== request.id));
            } else {
                setDeleteResultMessage(data.error || 'Only requests in Draft state can be deleted.');
                setDeleteSuccess(false);
            }
        } catch (error) {
            console.error('❌ Delete error:', error);

            if (error.message.includes('403')) {
                setDeleteResultMessage('Only requests in Draft state can be deleted.');
            } else if (error.message.includes('404')) {
                setDeleteResultMessage('Request not found. It may have been already deleted.');
            } else {
                setDeleteResultMessage('Error occurred while deleting the request.');
            }

            setDeleteSuccess(false);
        }

        setDeleteResultDialogOpen(true);
    };

    // REPLACE the handleFulfillmentUpdates function:
    // REPLACE ONLY the handleFulfillmentUpdates function:

    const handleFulfillmentUpdates = async (requestId) => {
        try {
            console.log('📊 === FULFILLMENT UPDATES CLICKED ===');

            const request = requests.find(req => req.id === requestId);
            if (!request) {
                console.error('❌ Request not found for fulfillment updates, ID:', requestId);
                return;
            }

            console.log('🎯 Found request:', {
                composite_id: request.id,
                original_id: request.original_id,
                source: request.source
            });

            let data;
            if (request.source === 'agn_firewall_requests') {
                const endpoint = `/api/agn-firewall/${request.original_id}`;
                console.log('🟠 Fetching fulfillment from AGN endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            } else if (request.source === 'webex_decommissioning_requests') {
                const endpoint = `/api/webex-decommissioning/${request.original_id}`;
                data = await ApiService.get(endpoint);
            } else if (request.source === 'ipt_decommissioning_requests') {
                const endpoint = `/api/ipt-decommissioning/${request.original_id}`;
                data = await ApiService.get(endpoint);
            } else if (request.source === 'circuit_order_requests') {
                const endpoint = `/api/circuit-orders/${request.original_id}`;
                console.log('🟢 Fetching fulfillment from Circuit Orders endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            } else {
                const endpoint = `/api/requests/${request.original_id}`;
                console.log('🔵 Fetching fulfillment from Bandwidth endpoint:', endpoint);
                data = await ApiService.get(endpoint);
            }

            // 🔧 FIX: Handle both naming conventions (snake_case and camelCase)
            const updatesField = data.fulfillment_updates || data.fulfillmentUpdates;
            console.log('🔍 Updates field value:', updatesField);

            // Handle both JSON string and already-parsed array
            let updates = [];
            if (updatesField) {
                if (typeof updatesField === 'string') {
                    updates = JSON.parse(updatesField);
                } else if (Array.isArray(updatesField)) {
                    updates = updatesField;
                } else {
                    console.warn('⚠️ Unexpected updates field type:', typeof updatesField);
                }
            }
            setCurrentFulfillmentUpdates(updates);
            setSelectedFulfillmentRequestId(request.original_id);
            setFulfillmentDialogOpen(true);
            console.log('✅ Fulfillment dialog opened with', updates.length, 'updates');
        } catch (error) {
            console.error('❌ Error fetching fulfillment updates:', error);
        }
    };

    const handleFulfillmentUpdateAdded = (newUpdates) => {
        setCurrentFulfillmentUpdates(newUpdates);
    };

    // New handler function for reviewing quotes
    // REPLACE the handleReviewQuote function:
    // REPLACE the handleReviewQuote function in TrackRequests.js:
const handleReviewQuote = async (request) => {
    try {
        setSelectedQuoteRequest(request);

        // Use original_id instead of composite id
        const requestId = request.original_id || request.id;

        console.log(`🔍 Fetching quote details for request ID: ${requestId}`);

        // Circuit Ordering uses its own comparison endpoint
        if (request.source === 'circuit_order_requests') {
            try {
                const comparison = await ApiService.getCircuitOrderComparison(requestId);
                setQuoteData({ circuitComparison: comparison, isCircuitOrder: true });
                setQuoteReviewDialogOpen(true);
            } catch (comparisonError) {
                console.error('❌ Error fetching circuit order comparison:', comparisonError);
                const message = comparisonError.status === 404
                    ? 'Vendor quote comparison is not yet available for this request. It will appear once a vendor quote has been received.'
                    : `Failed to load quote comparison: ${comparisonError.message}`;
                setResultMessage(message);
                setResultSuccess(false);
                setResultDialogOpen(true);
            }
            return;
        }

        // Determine correct endpoint based on request source
        let endpoint;
        if (request.source === 'agn_firewall_requests') {
            endpoint = `/api/agn-firewall/${requestId}`;
        } else if (request.source === 'webex_decommissioning_requests') {
            endpoint = `/api/webex-decommissioning/${requestId}`;
        } else if (request.source === 'ipt_decommissioning_requests') {
            endpoint = `/api/ipt-decommissioning/${requestId}`;
        } else {
            endpoint = `/api/requests/${requestId}`;
        }
        
        // Fetch request details
        const requestData = await ApiService.get(endpoint);
        console.log(`✅ Received response data:`, requestData);
        
        // Parse financial data directly from request_details_json
        let quoteDetails = null;
        
        if (requestData.request_details_json) {
            try {
                // Parse the JSON string into an object
                const requestDetailsJson = JSON.parse(requestData.request_details_json);
                const financial = requestDetailsJson.financial || {};
                
                console.log('📊 Parsed financial data:', financial);
                
                // Map financial data to the format expected by the UI
                quoteDetails = {
                    total: financial.total_cost || 0,
                    breakdown: {
                        sdWanCircuits: financial.cost_breakdown?.sd_wan_circuits || 0,
                        sdWanManagement: financial.cost_breakdown?.sd_wan_management || 0,
                        sdWanRouter: financial.cost_breakdown?.sd_wan_router || 0,
                        sdWanImacd: financial.cost_breakdown?.sd_wan_imacd || 0,
                        accentureEffort: financial.cost_breakdown?.accenture_effort || 0,
                        sdWanEffort: financial.cost_breakdown?.sd_wan_effort || 348.14,
                        odmTeamEffort: financial.cost_breakdown?.odm_team_effort || 924.00
                    },
                    details: {
                        circuitDetails: financial.circuit_details || [],
                        countryUplift: financial.country_uplift || 0
                    },
                    standard: financial.is_standard !== false // Default to true unless explicitly false
                };
                
                console.log(`✅ Mapped financial details to UI format:`, quoteDetails);
            } catch (parseError) {
                console.error('❌ Error parsing request_details_json:', parseError);
                // Fall back to old method
                quoteDetails = fallbackParseQuoteDetails(requestData);
            }
        } else {
            // Fall back to old method for backward compatibility
            quoteDetails = fallbackParseQuoteDetails(requestData);
        }
        
        setQuoteData(quoteDetails);
        setQuoteReviewDialogOpen(true);
        
    } catch (error) {
        console.error('❌ Error fetching quote details:', error);
        setResultMessage(`Failed to load quote details: ${error.message}`);
        setResultSuccess(false);
        setResultDialogOpen(true);
    }
};

// Helper function for backward compatibility
const fallbackParseQuoteDetails = (requestData) => {
    if (requestData.costEstimateDetails) {
        try {
            return JSON.parse(requestData.costEstimateDetails);
        } catch (parseError) {
            console.error('❌ Error parsing costEstimateDetails:', parseError);
        }
    }
    
    // Default structure if we can't parse
    return {
        total: parseFloat(requestData.costEstimate || 0),
        breakdown: {
            sdWanCircuits: 0,
            sdWanManagement: 0,
            sdWanRouter: 0,
            sdWanImacd: 0,
            accentureEffort: 0,
            sdWanEffort: 348.14,
            odmTeamEffort: 924.00
        },
        standard: requestData.standard !== 'No'
    };
};
    // Handler for approving quotes
const handleApproveQuote = async () => {
    setIsApproving(true);
    try {
        // Circuit order uses dedicated oe-approval endpoint
        if (selectedQuoteRequest.source === 'circuit_order_requests') {
            await ApiService.approveCircuitOrderQuote(selectedQuoteRequest.original_id, 'Approved');
            setRequests(prev => prev.map(r =>
                r.id === selectedQuoteRequest.id
                    ? { ...r, approval_status: 'Approved', oe_approval_status: 'Approved' }
                    : r
            ));
            setQuoteReviewDialogOpen(false);
            setResultMessage('Quote approved successfully! The request will now proceed.');
            setResultSuccess(true);
            setResultDialogOpen(true);
            return;
        }

        // 1. Update the request status via the API - USE ORIGINAL_ID INSTEAD OF ID
        await ApiService.patch(`/api/generic/update/requests/${selectedQuoteRequest.original_id}`, {
            approval_status: 'Approved',
            request_status: 'Work in Progress'
        });

        // 2. Get the request details JSON to extract amelia_ticket_id and amelia_subject
        let endpoint;
        if (selectedQuoteRequest.source === 'agn_firewall_requests') {
            endpoint = `/api/agn-firewall/${selectedQuoteRequest.original_id}`;
        } else if (selectedQuoteRequest.source === 'webex_decommissioning_requests') {
            endpoint = `/api/webex-decommissioning/${selectedQuoteRequest.original_id}`;
        } else if (selectedQuoteRequest.source === 'ipt_decommissioning_requests') {
            endpoint = `/api/ipt-decommissioning/${selectedQuoteRequest.original_id}`;
        } else {
            endpoint = `/api/requests/${selectedQuoteRequest.original_id}`;
        }
        const requestDetails = await ApiService.get(endpoint);
        let ameliaTicketId = null;
        let ameliaSubject = null;
        
        if (requestDetails.request_details_json) {
            try {
                const parsedJson = JSON.parse(requestDetails.request_details_json);
                ameliaTicketId = parsedJson.amelia_ticket_id || parsedJson.status_tracking?.amelia_ticket_id;
                ameliaSubject = parsedJson.amelia_subject;
                console.log('📊 Found Amelia ticket ID:', ameliaTicketId);
                console.log('📊 Found Amelia subject:', ameliaSubject);
            } catch (parseError) {
                console.error('❌ Error parsing request_details_json:', parseError);
            }
        }

        // 3. Send notification email to Amelia
        const emailData = {
            odm_portal_request_id: selectedQuoteRequest.original_id,
            amelia_request_id: ameliaTicketId,
            purpose: "Portal to Amelia - Customer Approved Quote",
            to: ["aca-aiops-uat@allianz.com"],
            cc: [],
            subject: `ODM Quote Approval: ${selectedQuoteRequest.odm_title || 'N/A'}`,
            amelia_original_subject: ameliaSubject
        };
        
        console.log('📧 Sending quote approval email to Amelia:', emailData);
        await ApiService.post('/api/notifications/send-email', emailData);
        console.log('✅ Email notification sent successfully');

        // 4. Update the local state to reflect changes (maintain sort order)
        setRequests(prevRequests => {
            const updated = prevRequests.map(req =>
                req.id === selectedQuoteRequest.id
                    ? { ...req, approval_status: 'Approved', request_status: 'Work in Progress' }
                    : req
            );
            // Maintain sort order after update
            return updated.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.submissionDate || a.created_at || a.createdAt || 0);
                const dateB = new Date(b.submission_date || b.submissionDate || b.created_at || b.createdAt || 0);
                return dateB - dateA;
            });
        });

        // 5. Close the quote review dialog and show success message
        setQuoteReviewDialogOpen(false);
        setResultMessage('Quote approved successfully! The request will now proceed to implementation.');
        setResultSuccess(true);
        setResultDialogOpen(true);
    } catch (error) {
        console.error('❌ Error approving quote:', error);
        setResultMessage(`Failed to approve quote: ${error.message}`);
        setResultSuccess(false);
        setResultDialogOpen(true);
    } finally {
        setIsApproving(false);
    }
};

    // Handler for rejecting quotes
    const handleRejectQuote = async () => {
        setIsRejecting(true);
        try {
            if (selectedQuoteRequest.source === 'circuit_order_requests') {
                await ApiService.approveCircuitOrderQuote(selectedQuoteRequest.original_id, 'Rejected');
                setRequests(prev => prev.map(r =>
                    r.id === selectedQuoteRequest.id
                        ? { ...r, approval_status: 'Quote Rejected', oe_approval_status: 'Rejected' }
                        : r
                ));
                setQuoteReviewDialogOpen(false);
                setResultMessage('Quote rejected. Your account manager will be notified.');
                setResultSuccess(false);
                setResultDialogOpen(true);
                return;
            }

            // Update the request status via the API - USE ORIGINAL_ID INSTEAD OF COMPOSITE ID
            await ApiService.patch(`/api/generic/update/requests/${selectedQuoteRequest.original_id}`, {
                approval_status: 'Quote Rejected',
                request_status: 'On Hold'
            });

            // Update the local state (maintain sort order)
            setRequests(prevRequests => {
                const updated = prevRequests.map(req =>
                    req.id === selectedQuoteRequest.id
                        ? { ...req, approval_status: 'Quote Rejected', request_status: 'On Hold' }
                        : req
                );
                // Maintain sort order after update
                return updated.sort((a, b) => {
                    const dateA = new Date(a.submission_date || a.submissionDate || a.created_at || a.createdAt || 0);
                    const dateB = new Date(b.submission_date || b.submissionDate || b.created_at || b.createdAt || 0);
                    return dateB - dateA;
                });
            });

            // Close the dialog and show message
            setQuoteReviewDialogOpen(false);
            setResultMessage('Quote rejected. Your account manager will be notified and will contact you for next steps.');
            setResultSuccess(false);
            setResultDialogOpen(true);
        } catch (error) {
            console.error('Error rejecting quote:', error);
            setResultMessage(`Failed to reject quote: ${error.message}`);
            setResultSuccess(false);
            setResultDialogOpen(true);
        } finally {
            setIsRejecting(false);
        }
    };

    // Define columns for the DataGrid with a modified options column for the Review Quote button
    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'odm_title', headerName: 'ODM Title', width: 200 },
        { field: 'request_type', headerName: 'Request Type', width: 200 },
        { field: 'oe_name', headerName: 'OE Name', width: 180 },
        { field: 'request_state', headerName: 'Request State', width: 150 },
        { field: 'request_status', headerName: 'Request Status', width: 150 },
        { field: 'cost_estimate', headerName: 'Cost Estimate', width: 150 },
        { field: 'approval_status', headerName: 'Approval Status', width: 150 },
        {
            field: 'acai_request_id',
            headerName: 'Amelia Task ID',
            width: 150,
            renderCell: (params) => {
                const ameliaId = params.row?.acai_request_id || params.row?.acaiRequestId;
                if (!ameliaId) {
                    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>;
                }
                return (
                    <span style={{ fontWeight: 500, color: '#1e293b' }}>
                        {ameliaId}
                    </span>
                );
            }
        },
        {
            field: 'submission_date',
            headerName: 'Submission Date',
            width: 180,
            renderCell: (params) => {
                // Get the submission date from row
                const dateValue = params.row?.submission_date ||
                    params.row?.submissionDate ||
                    params.row?.created_at ||
                    params.row?.createdAt ||
                    params.row?.initiation_date ||
                    params.row?.initiationDate;

                if (!dateValue) return <span style={{ color: '#9ca3af' }}>N/A</span>;

                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return <span style={{ color: '#9ca3af' }}>N/A</span>;

                const now = new Date();
                const diffMs = now - date;
                const diffHours = Math.floor(diffMs / 3600000);
                const diffDays = Math.floor(diffMs / 86400000);

                let relativeTime = '';
                if (diffHours < 1) {
                    relativeTime = 'Just now';
                } else if (diffHours < 24) {
                    relativeTime = `${diffHours}h ago`;
                } else if (diffDays < 7) {
                    relativeTime = `${diffDays}d ago`;
                } else {
                    relativeTime = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                }

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1e293b' }}>
                            {relativeTime}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            {date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            })}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'options',
            headerName: 'Options',
            width: 450, // Increased width from 400 to 450
            renderCell: (params) => (
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap', // Allow wrapping to multiple rows
                    gap: 1,
                    width: '100%' // Use full width of the cell
                }}>
                    {/* Primary actions in the top row */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Button
                            onClick={() => handleView(params.row)}
                            variant="contained"
                            size="small"
                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                        >
                            View
                        </Button>
                        <Button
                            onClick={() => handleUpdate(params.row)}
                            variant="contained"
                            color="warning"
                            size="small"
                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                        >
                            Update
                        </Button>
                        <Button
                            onClick={() => handleDelete(params.row)}
                            variant="contained"
                            color="error"
                            size="small"
                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                        >
                            Delete
                        </Button>
                    </Box>

                    {/* Secondary actions in the bottom row */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            onClick={() => handleFulfillmentUpdates(params.row.id)}
                            variant="outlined"
                            color="success"
                            size="small"
                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                        >
                            Updates
                        </Button>

                        {/* Quote Review button */}
                        {(params.row.approval_status === 'Quote Acquired' ||
                          (params.row.source === 'circuit_order_requests' && params.row.approval_status === 'Vendor Selected')) && (
                            <Button
                                onClick={() => handleReviewQuote(params.row)}
                                variant="contained"
                                color="secondary"
                                size="small"
                                startIcon={<AssessmentIcon fontSize="small" />}
                                sx={{ fontSize: '0.75rem' }}
                            >
                                Review Quote
                            </Button>
                        )}

                        {/* Download Excel button for Webex and IPT decommissioning requests */}
                        {(params.row.source === 'webex_decommissioning_requests' || params.row.source === 'ipt_decommissioning_requests') && params.row.has_excel_attachment && (
                            <Button
                                onClick={() => handleDownloadExcel(params.row)}
                                variant="contained"
                                color="info"
                                size="small"
                                sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                            >
                                Download Excel
                            </Button>
                        )}
                    </Box>
                </Box>
            ),
        },
    ];

    // Filter requests based on current tab and search query
    const filteredRequests = React.useMemo(() => {
        // First, filter by tab
        let filtered = requests;

        if (currentTab !== 'all') {
            const filterMap = {
                'bandwidth': (req) => req.source === 'requests',
                'firewall': (req) => req.source === 'agn_firewall_requests',
                'webex': (req) => req.source === 'webex_decommissioning_requests',
                'ipt': (req) => req.source === 'ipt_decommissioning_requests',
                'circuit': (req) => req.source === 'circuit_order_requests'
            };
            filtered = requests.filter(filterMap[currentTab] || (() => true));
        }

        // Then, apply search filter if search query exists
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(req => {
                // Search across all relevant fields
                const searchableFields = [
                    req.odm_title,
                    req.request_type,
                    req.oe_name,
                    req.request_state,
                    req.request_status,
                    req.cost_estimate,
                    req.approval_status,
                    req.acai_request_id || req.acaiRequestId,
                    req.grm_demand_number,
                    req.submission_date,
                    req.customer_name,
                    req.city
                ];

                return searchableFields.some(field =>
                    field && field.toString().toLowerCase().includes(query)
                );
            });
        }

        return filtered;
    }, [requests, currentTab, searchQuery]);

    // Calculate counts for each tab
    const requestCounts = React.useMemo(() => {
        return {
            all: requests.length,
            bandwidth: requests.filter(r => r.source === 'requests').length,
            firewall: requests.filter(r => r.source === 'agn_firewall_requests').length,
            webex: requests.filter(r => r.source === 'webex_decommissioning_requests').length,
            ipt: requests.filter(r => r.source === 'ipt_decommissioning_requests').length,
            circuit: requests.filter(r => r.source === 'circuit_order_requests').length
        };
    }, [requests]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    return (
        <Layout>
            <Box sx={{ width: '100%', mt: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                    <center>Track Your Requests</center>
                </Typography>

                {loading ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px'
                    }}>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{ color: '#003781' }}
                        />
                    </Box>
                ) : (
                    <>
                        {/* Clean Scrollable Tabs for Request Types with Search */}
                        <Box
                            sx={{
                                mb: 3,
                                borderBottom: '2px solid #e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap'
                            }}
                        >
                            <Tabs
                                value={currentTab}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 48,
                                    '& .MuiTabs-indicator': {
                                        height: 3,
                                        backgroundColor: '#003781',
                                        borderRadius: '3px 3px 0 0'
                                    },
                                    '& .MuiTabs-scrollButtons': {
                                        color: '#003781',
                                        '&.Mui-disabled': {
                                            opacity: 0.3
                                        }
                                    },
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        minHeight: 48,
                                        px: 3,
                                        color: '#6b7280',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            color: '#003781',
                                            backgroundColor: 'rgba(0, 55, 129, 0.04)'
                                        },
                                        '&.Mui-selected': {
                                            color: '#003781',
                                            fontWeight: 600
                                        }
                                    }
                                }}
                            >
                                <Tab
                                    value="all"
                                    icon={<AllInclusive sx={{ fontSize: 20 }} />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>All Requests</span>
                                            <Chip
                                                label={requestCounts.all}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: currentTab === 'all' ? '#003781' : '#e5e7eb',
                                                    color: currentTab === 'all' ? 'white' : '#6b7280',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="bandwidth"
                                    icon={<NetworkCheck sx={{ fontSize: 20 }} />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>Bandwidth Upgrade/Downgrade</span>
                                            <Chip
                                                label={requestCounts.bandwidth}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: currentTab === 'bandwidth' ? '#003781' : '#e5e7eb',
                                                    color: currentTab === 'bandwidth' ? 'white' : '#6b7280',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="firewall"
                                    icon={<Security sx={{ fontSize: 20 }} />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>AGN Firewall</span>
                                            <Chip
                                                label={requestCounts.firewall}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: currentTab === 'firewall' ? '#003781' : '#e5e7eb',
                                                    color: currentTab === 'firewall' ? 'white' : '#6b7280',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="webex"
                                    icon={<PhoneDisabled sx={{ fontSize: 20 }} />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>Webex Decommissioning</span>
                                            <Chip
                                                label={requestCounts.webex}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: currentTab === 'webex' ? '#003781' : '#e5e7eb',
                                                    color: currentTab === 'webex' ? 'white' : '#6b7280',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="ipt"
                                    icon={<Router sx={{ fontSize: 20 }} />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>IPT Decommissioning</span>
                                            <Chip
                                                label={requestCounts.ipt}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: currentTab === 'ipt' ? '#003781' : '#e5e7eb',
                                                    color: currentTab === 'ipt' ? 'white' : '#6b7280',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="circuit"
                                    icon={<Layers sx={{ fontSize: 20 }} />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>Circuit Ordering</span>
                                            <Chip
                                                label={requestCounts.circuit}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: currentTab === 'circuit' ? '#003781' : '#e5e7eb',
                                                    color: currentTab === 'circuit' ? 'white' : '#6b7280',
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                            </Tabs>

                            {/* Beautiful Search Bar */}
                            <Box sx={{
                                width: { xs: '100%', sm: '380px', md: '450px' },
                                mb: { xs: 2, sm: 0 },
                                position: 'relative'
                            }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Search by ODM title, Request type, OE Name, state, status, Amelia Task ID, cost..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{
                                                    color: '#003781',
                                                    fontSize: 22,
                                                    transition: 'all 0.2s ease'
                                                }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchQuery && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSearchQuery('')}
                                                    edge="end"
                                                    sx={{
                                                        color: '#64748b',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: '#e2e8f0',
                                                            color: '#003781',
                                                            transform: 'rotate(90deg)'
                                                        }
                                                    }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff',
                                            borderRadius: '10px',
                                            fontSize: '0.875rem',
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                            '& fieldset': {
                                                borderColor: '#e2e8f0',
                                                borderWidth: '2px',
                                                transition: 'all 0.3s ease',
                                            },
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(0, 55, 129, 0.1)',
                                                transform: 'translateY(-1px)',
                                                '& fieldset': {
                                                    borderColor: '#003781',
                                                },
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: '0 4px 16px rgba(0, 55, 129, 0.15)',
                                                transform: 'translateY(-1px)',
                                                '& fieldset': {
                                                    borderColor: '#003781',
                                                    borderWidth: '2px',
                                                },
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '10px 12px',
                                            '&::placeholder': {
                                                color: '#94a3b8',
                                                opacity: 1,
                                                fontSize: '0.8125rem',
                                                fontWeight: 400
                                            }
                                        }
                                    }}
                                />
                                {searchQuery && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: 0,
                                            mt: 0.5,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '6px',
                                            backgroundColor: '#f1f5f9',
                                            border: '1px solid #e2e8f0',
                                            animation: 'fadeIn 0.2s ease-in'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#003781',
                                                    color: 'white',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {filteredRequests.length}
                                            </Box>
                                            result{filteredRequests.length !== 1 ? 's' : ''} found
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* DataGrid with filtered requests */}
                        {filteredRequests.length > 0 ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid #e2e8f0'
                                }}
                            >
                                <DataGrid
                                    rows={filteredRequests}
                                    columns={columns}
                                    pageSize={5}
                                    rowsPerPageOptions={[5, 10, 20]}
                                    checkboxSelection
                                    disableColumnMenu={false}
                                    sortingOrder={['desc', 'asc']}
                                    initialState={{
                                        sorting: {
                                            sortModel: []
                                        }
                                    }}
                                    sx={{
                                        height: 650,
                                        border: 'none',
                                        '& .MuiDataGrid-row': {
                                            marginBottom: '10px',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                            bgcolor: '#f8fafc',
                                            borderBottom: '2px solid #e2e8f0'
                                        }
                                    }}
                                />
                            </Paper>
                        ) : (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    borderRadius: 2,
                                    border: '1px solid #e2e8f0',
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        bgcolor: '#e0e7ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px'
                                    }}
                                >
                                    <Description sx={{ fontSize: 40, color: '#6366f1' }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                                    No {currentTab === 'all' ? '' : currentTab} requests found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {searchQuery
                                        ? `No results found for "${searchQuery}". Try adjusting your search.`
                                        : currentTab === 'all'
                                            ? 'No requests available to display.'
                                            : 'Try selecting a different request type from the tabs above.'}
                                </Typography>
                                {searchQuery && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setSearchQuery('')}
                                        sx={{ mt: 2 }}
                                    >
                                        Clear Search
                                    </Button>
                                )}
                            </Paper>
                        )}
                    </>
                )}
            </Box>

            {/* Beautified View Dialog */}

            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                aria-labelledby="view-request-dialog-title"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                        overflow: 'hidden'
                    }
                }}
                TransitionComponent={Fade}
                transitionDuration={400}
            >
                <DialogTitle
                    id="view-request-dialog-title"
                    sx={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        color: 'white',
                        pb: 2.5,
                        pt: 2,
                        px: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
                            pointerEvents: 'none'
                        }
                    }}
                >
                    <Box position="relative" zIndex={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 6px 12px rgba(59, 130, 246, 0.25)'
                                    }}
                                >
                                    <Description sx={{ fontSize: 24, color: 'white' }} />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            letterSpacing: '-0.3px',
                                            mb: 0.25,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        Request Details
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            opacity: 0.85,
                                            fontWeight: 400,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {selectedRequest?.odm_title || selectedRequest?.odmTitle || 'Complete request information'}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => setViewDialogOpen(false)}
                                size="small"
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                        {selectedRequest && (
                            <Stack direction="row" spacing={1} mt={1.5}>
                                {selectedRequest.request_state || selectedRequest.requestState ? (
                                    <Chip
                                        label={selectedRequest.request_state || selectedRequest.requestState}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            fontSize: '0.7rem',
                                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                                            color: 'white',
                                            fontWeight: 600,
                                            border: '1px solid rgba(255, 255, 255, 0.3)'
                                        }}
                                    />
                                ) : null}
                                {selectedRequest.approval_status || selectedRequest.approvalStatus ? (
                                    <Chip
                                        label={selectedRequest.approval_status || selectedRequest.approvalStatus}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            fontSize: '0.7rem',
                                            bgcolor: 'rgba(16, 185, 129, 0.2)',
                                            color: 'white',
                                            fontWeight: 600,
                                            border: '1px solid rgba(16, 185, 129, 0.4)'
                                        }}
                                    />
                                ) : null}
                            </Stack>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        p: 2.5,
                        backgroundColor: '#f8fafc'
                    }}
                >
                    {selectedRequest && (() => {
                                const isAGNFirewall = selectedRequest.source === 'agn_firewall_requests';
                                const isWebexDecommissioning = selectedRequest.source === 'webex_decommissioning_requests';
                                const isIPTDecommissioning = selectedRequest.source === 'ipt_decommissioning_requests';
                                const isCircuitOrder = selectedRequest.source === 'circuit_order_requests';

                                // Determine material number based on request type
                                let materialNumberInfo = null;

                                // Try to extract material number from cost_estimate_details if available
                                if (selectedRequest.cost_estimate_details || selectedRequest.costEstimateDetails) {
                                    try {
                                        const costDetails = JSON.parse(
                                            selectedRequest.cost_estimate_details || selectedRequest.costEstimateDetails
                                        );
                                        if (costDetails.breakdown?.materialNumber) {
                                            materialNumberInfo = {
                                                number: costDetails.breakdown.materialNumber,
                                                description: costDetails.breakdown.materialDescription || ''
                                            };
                                        } else if (costDetails.breakdown?.materialNumbers?.all) {
                                            materialNumberInfo = {
                                                number: costDetails.breakdown.materialNumbers.all,
                                                description: costDetails.breakdown.serviceDescription || ''
                                            };
                                        }
                                    } catch (e) {
                                        // If parsing fails, fall back to default logic
                                        console.log('Failed to parse cost_estimate_details:', e);
                                    }
                                }

                                // Also try to extract from request_details_json for AGN Firewall and other types
                                if (!materialNumberInfo && selectedRequest.request_details_json) {
                                    try {
                                        const requestDetails = typeof selectedRequest.request_details_json === 'string'
                                            ? JSON.parse(selectedRequest.request_details_json)
                                            : selectedRequest.request_details_json;

                                        if (requestDetails.financial?.material_number) {
                                            materialNumberInfo = {
                                                number: requestDetails.financial.material_number,
                                                description: requestDetails.financial.material_description || ''
                                            };
                                        }
                                    } catch (e) {
                                        // If parsing fails, fall back to default logic
                                        console.log('Failed to parse request_details_json:', e);
                                    }
                                }

                                // Fall back to default material numbers based on request type
                                if (!materialNumberInfo) {
                                    if (isWebexDecommissioning) {
                                        materialNumberInfo = {
                                            number: materialNumbers.webex.primary,
                                            description: materialNumbers.webex.description
                                        };
                                    } else if (isIPTDecommissioning) {
                                        materialNumberInfo = {
                                            number: materialNumbers.ipt.primary,
                                            description: materialNumbers.ipt.description
                                        };
                                    } else if (isAGNFirewall) {
                                        materialNumberInfo = {
                                            number: materialNumbers.agnFirewall.primary,
                                            description: materialNumbers.agnFirewall.description
                                        };
                                    } else if (selectedRequest.request_type === 'Bandwidth Upgrade/Downgrade') {
                                        materialNumberInfo = {
                                            number: 'Multiple',
                                            description: 'SD-WAN Circuit (908138), Management (908134), Router (908137), IMACD (908374), Integrator (905513)'
                                        };
                                    }
                                }

                                // Add material number to selectedRequest for field rendering
                                if (materialNumberInfo) {
                                    selectedRequest.materialNumber = materialNumberInfo.number;
                                    selectedRequest.materialNumberDescription = materialNumberInfo.description;
                                }

                                // Helper function to get field value
                                const getValue = (camelCase, snakeCase) => {
                                    return selectedRequest[camelCase] || selectedRequest[snakeCase];
                                };

                                // Helper to format field labels
                                const formatLabel = (key) => {
                                    const labelMap = {
                                        numberOfSIUsers: 'Number of SI Users',
                                        numberOfDDI: 'Number of DDI',
                                        grmNo: 'GRM No',
                                        grmDemandNumber: 'GRM Demand Number',
                                        grm_demand_number: 'GRM Demand Number',
                                        isMassDecomm: 'Is Mass Decommissioning',
                                        notificationSentTo: 'Notification Sent To',
                                        acaiRequestId: 'ACAI Request ID',
                                        acai_request_id: 'ACAI Request ID',
                                        odmTitle: 'ODM Title',
                                        odm_title: 'ODM Title',
                                        odmType: 'ODM Type',
                                        oeName: 'OE Name',
                                        oe_name: 'OE Name',
                                        costCentre: 'Cost Centre',
                                        cost_centre: 'Cost Centre',
                                        desiredDateOfInstallation: 'Desired Date of Installation',
                                        desired_date_of_installation: 'Desired Date of Installation',
                                        descriptionOfOdmRequest: 'Description of ODM Request',
                                        description_of_odm_request: 'Description of ODM Request',
                                        businessReasons: 'Business Reasons',
                                        business_reasons: 'Business Reasons',
                                        currentSituation: 'Current Situation',
                                        futureSituation: 'Future Situation',
                                        customerName: 'Customer Name',
                                        customer_name: 'Customer Name',
                                        customerContactMail: 'Customer Contact Email',
                                        customer_contact_mail: 'Customer Contact Email',
                                        customerContactPhone: 'Customer Contact Phone',
                                        customer_contact_phone: 'Customer Contact Phone',
                                        siteTechnicalUserName: 'Site Technical User Name',
                                        site_technical_user_name: 'Site Technical User Name',
                                        siteTechnicalUserEmail: 'Site Technical User Email',
                                        site_technical_user_email: 'Site Technical User Email',
                                        siteTechnicalUserPhone: 'Site Technical User Phone',
                                        site_technical_user_phone: 'Site Technical User Phone',
                                        requestState: 'Request State',
                                        request_state: 'Request State',
                                        requestStatus: 'Request Status',
                                        request_status: 'Request Status',
                                        approvalStatus: 'Approval Status',
                                        approval_status: 'Approval Status',
                                        costEstimate: 'Cost Estimate',
                                        cost_estimate: 'Cost Estimate',
                                        submissionDate: 'Submission Date',
                                        submission_date: 'Submission Date',
                                        siteAddress: 'Site Address',
                                        site_address: 'Site Address',
                                        countryType: 'Country Type',
                                        country_type: 'Country Type',
                                        excelFileName: 'Excel File Name',
                                        excel_file_name: 'Excel File Name',
                                        excelFileSize: 'Excel File Size',
                                        serviceType: 'Service Type',
                                        hardwareServiceType: 'Hardware Service Type',
                                        initiationDate: 'Initiation Date'
                                    };
                                    return labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()
                                        .split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
                                };

                                // Define field groups for better organization
                                const standardFieldGroups = {
                                    basic: {
                                        title: 'Basic Information',
                                        icon: ViewModule,
                                        fields: [
                                            { key: 'id', label: 'ID' },
                                            { key: ['odmTitle', 'odm_title'], label: 'ODM Title' },
                                            { key: ['odmType', 'request_type'], label: 'ODM Type' },
                                            { key: ['grmDemandNumber', 'grm_demand_number', 'grmNo'], label: 'GRM Number' },
                                            { key: ['submissionDate', 'submission_date', 'initiationDate'], label: 'Submission Date' },
                                            { key: ['requestState', 'request_state'], label: 'Request State' },
                                            { key: ['requestStatus', 'request_status'], label: 'Request Status' },
                                            { key: ['approvalStatus', 'approval_status'], label: 'Approval Status' }
                                        ]
                                    },
                                    location: {
                                        title: 'Location Details',
                                        icon: LocationOn,
                                        fields: [
                                            { key: 'site_id', label: 'Site ID' },
                                            { key: ['oeName', 'oe_name'], label: 'OE Name' },
                                            { key: ['siteAddress', 'site_address'], label: 'Site Address' },
                                            { key: ['city', 'location'], label: 'City/Location' },
                                            { key: 'country', label: 'Country' },
                                            { key: 'countryType', label: 'Country Type' }
                                        ]
                                    },
                                    financial: {
                                        title: 'Financial Information',
                                        icon: AttachMoney,
                                        fields: [
                                            { key: ['costCentre', 'cost_centre'], label: 'Cost Centre' },
                                            { key: 'debtor', label: 'Debtor' },
                                            { key: ['costEstimate', 'cost_estimate'], label: 'Cost Estimate' },
                                            { key: 'materialNumber', label: 'Material Number', type: 'material' },
                                            { key: 'standard', label: 'Standard' }
                                        ]
                                    },
                                    contacts: {
                                        title: 'Contact Information',
                                        icon: Person,
                                        fields: [
                                            { key: ['customerName', 'customer_name'], label: 'Customer Name' },
                                            { key: ['customerContactMail', 'customer_contact_mail'], label: 'Customer Email' },
                                            { key: ['customerContactPhone', 'customer_contact_phone'], label: 'Customer Phone' },
                                            { key: ['siteTechnicalUserName', 'site_technical_user_name'], label: 'Technical User Name' },
                                            { key: ['siteTechnicalUserEmail', 'site_technical_user_email'], label: 'Technical User Email' },
                                            { key: ['siteTechnicalUserPhone', 'site_technical_user_phone'], label: 'Technical User Phone' }
                                        ]
                                    },
                                    technical: {
                                        title: 'Technical Details',
                                        icon: Layers,
                                        fields: [
                                            { key: ['desiredDateOfInstallation', 'desired_date_of_installation'], label: 'Desired Date of Installation' },
                                            { key: ['currentSituation', 'current_provider'], label: 'Current Situation/Provider' },
                                            { key: ['futureSituation', 'new_bandwidth'], label: 'Future Situation/Bandwidth' },
                                            { key: 'current_bandwidth', label: 'Current Bandwidth' },
                                            { key: 'available_circuit', label: 'Available Circuit' },
                                            { key: 'link_type', label: 'Link Type' },
                                            { key: 'number_of_links', label: 'Number of Links' },
                                            { key: 'router_model', label: 'Router Model' },
                                            { key: 'numberOfSIUsers', label: 'Number of SI Users' },
                                            { key: 'numberOfDDI', label: 'Number of DDI' },
                                            { key: 'isMassDecomm', label: 'Is Mass Decommissioning', type: 'boolean' },
                                            { key: 'serviceType', label: 'Service Type' },
                                            { key: 'hardwareServiceType', label: 'Hardware Service Type' },
                                            { key: 'excelFileName', label: 'Excel File Name' }
                                        ]
                                    },
                                    description: {
                                        title: 'Description & Reasons',
                                        icon: Description,
                                        fields: [
                                            { key: ['descriptionOfOdmRequest', 'description_of_odm_request'], label: 'Description of ODM Request', type: 'long' },
                                            { key: ['businessReasons', 'business_reasons'], label: 'Business Reasons', type: 'long' },
                                            { key: 'notificationSentTo', label: 'Notification Sent To' }
                                        ]
                                    },
                                    fulfillment: {
                                        title: 'Fulfillment Updates',
                                        icon: Schedule,
                                        fields: [
                                            { key: ['acaiRequestId', 'acai_request_id'], label: 'ACAI Request ID' },
                                            { key: ['fulfillmentUpdates', 'fulfillment_updates'], label: 'Updates', type: 'fulfillment' }
                                        ]
                                    },
                                    detailedInfo: {
                                        title: 'Detailed Request Information',
                                        icon: AssessmentIcon,
                                        fields: [
                                            { key: 'request_details_json', label: 'Request Details', type: 'json' }
                                        ]
                                    }
                                };

                                // Circuit Ordering lives in its own table (circuit_order_requests) with its own
                                // column names, so it needs its own groups — the standard ones are keyed to the
                                // bandwidth upgrade / decommissioning column names and would hide almost everything.
                                const circuitOrderFieldGroups = {
                                    basic: {
                                        title: 'Basic Information',
                                        icon: ViewModule,
                                        fields: [
                                            { key: 'id', label: 'ID' },
                                            { key: 'odm_title', label: 'ODM Title' },
                                            { key: 'request_type', label: 'ODM Type' },
                                            { key: 'grm_demand_number', label: 'GRM Number' },
                                            { key: 'accenture_cr_number', label: 'Accenture CR Number' },
                                            { key: 'accenture_request_id', label: 'Accenture Request ID' },
                                            { key: 'submission_date', label: 'Submission Date' },
                                            { key: 'request_state', label: 'Request State' },
                                            { key: 'request_status', label: 'Request Status' },
                                            { key: 'approval_status', label: 'Approval Status' }
                                        ]
                                    },
                                    location: {
                                        title: 'Site & Location',
                                        icon: LocationOn,
                                        fields: [
                                            { key: 'customer_site_id', label: 'Customer Site ID' },
                                            { key: 'location_id', label: 'Location ID (SecID)' },
                                            { key: 'install_site_address', label: 'Install Site Address', type: 'long' },
                                            { key: 'site_city', label: 'City' },
                                            { key: 'site_country', label: 'Country' },
                                            { key: 'site_type', label: 'Site Type' },
                                            { key: 'region', label: 'Region' },
                                            { key: 'is_new_site', label: 'New Site', type: 'boolean' },
                                            { key: 'oe_name', label: 'OE Name' }
                                        ]
                                    },
                                    circuit: {
                                        title: 'Circuit & Bandwidth',
                                        icon: NetworkCheck,
                                        fields: [
                                            { key: 'product', label: 'Product' },
                                            { key: 'existing_circuit_ids', label: 'Existing Circuit IDs', type: 'long' },
                                            { key: 'access_speed_mbps', label: 'Access Speed (Mbps)' },
                                            { key: 'access_speed_mbps_2', label: 'Access Speed 2 (Mbps)' },
                                            { key: 'access_speed_mbps_3', label: 'Access Speed 3 (Mbps)' },
                                            { key: 'port_speed_mbps', label: 'Port Speed (Mbps)' },
                                            { key: 'circuit_id', label: 'Circuit ID (at Implementation)' },
                                            { key: 'circuit_id_2', label: 'Circuit ID 2 (at Implementation)' },
                                            { key: 'local_provider_name', label: 'Local Provider' },
                                            { key: 'handoff_interface_type', label: 'Handoff Interface Type' },
                                            { key: 'lan_port_requirement', label: 'LAN Port Requirement' }
                                        ]
                                    },
                                    diversity: {
                                        title: 'Diversity Requirements',
                                        icon: AllInclusive,
                                        fields: [
                                            { key: 'diversity_requirement', label: 'Diversity Requirement' },
                                            { key: 'path_diversity', label: 'Path Diversity' },
                                            { key: 'dual_building_entry', label: 'Dual Building Entry' },
                                            { key: 'dual_building_entry_availability', label: 'Dual Building Entry Available at Site' },
                                            { key: 'dual_termination_points', label: 'Dual Termination Points' },
                                            { key: 'dual_diverse_pop', label: 'Dual Diverse PoP' },
                                            { key: 'dual_pe_router', label: 'Dual PE Router' },
                                            { key: 'dual_loop', label: 'Dual Loop' }
                                        ]
                                    },
                                    technical: {
                                        title: 'Site & Installation Details',
                                        icon: Layers,
                                        fields: [
                                            { key: 'customer_required_date', label: 'Customer Required Date' },
                                            { key: 'expedite_status', label: 'Expedite Status' },
                                            { key: 'implementation_approach', label: 'Implementation Approach' },
                                            { key: 'demarcation_location', label: 'Demarcation Location' },
                                            { key: 'technical_demarcation', label: 'Technical Demarcation', type: 'long' },
                                            { key: 'pop_name_location', label: 'PoP Name / Location' },
                                            { key: 'ip_address', label: 'IP Address' },
                                            { key: 'num_users', label: 'Number of Users' },
                                            { key: 'num_ip_phones', label: 'Number of IP Phones' },
                                            { key: 'num_vc_devices', label: 'Number of VC Devices' }
                                        ]
                                    },
                                    financial: {
                                        title: 'Financial Information',
                                        icon: AttachMoney,
                                        fields: [
                                            { key: 'cost_centre', label: 'Cost Centre' },
                                            { key: 'debtor', label: 'Debtor' },
                                            { key: 'po_number', label: 'PO Number' }
                                        ]
                                    },
                                    vendor: {
                                        title: 'Vendor & Approvals',
                                        icon: Business,
                                        fields: [
                                            { key: 'selected_vendor', label: 'Selected Vendor' },
                                            { key: 'gitp_selection_comment', label: 'GITP Selection Comment', type: 'long' },
                                            { key: 'strategic_approval_required', label: 'Strategic Approval Required', type: 'boolean' },
                                            { key: 'strategic_approval_status', label: 'Strategic Approval Status' },
                                            { key: 'oe_approval_status', label: 'OE Approval Status' }
                                        ]
                                    },
                                    quotes: {
                                        title: 'Quotes & Cost Breakdown',
                                        icon: AssessmentIcon,
                                        fields: [
                                            { key: 'quote_comparison', label: 'Quote Comparison', type: 'quotes' },
                                            { key: 'vendor_quotes', label: 'Vendor Quotes', type: 'quotes' },
                                            { key: 'benchmark_quote', label: 'Benchmark Quote', type: 'quotes' }
                                        ]
                                    },
                                    contacts: {
                                        title: 'Contact Information',
                                        icon: Person,
                                        fields: [
                                            { key: 'customer_name', label: 'Customer Name' },
                                            { key: 'customer_contact_mail', label: 'Customer Email' },
                                            { key: 'customer_contact_phone', label: 'Customer Phone' },
                                            { key: 'site_technical_user_name', label: 'Technical User Name' },
                                            { key: 'site_technical_user_email', label: 'Technical User Email' },
                                            { key: 'site_technical_user_phone', label: 'Technical User Phone' },
                                            { key: 'lcon_name', label: 'LCON Name' },
                                            { key: 'lcon_email', label: 'LCON Email' },
                                            { key: 'lcon_phone', label: 'LCON Phone' }
                                        ]
                                    },
                                    description: {
                                        title: 'Description & Reasons',
                                        icon: Description,
                                        fields: [
                                            { key: 'request_description', label: 'Request Description', type: 'long' },
                                            { key: 'business_reasons', label: 'Business Reasons', type: 'long' }
                                        ]
                                    },
                                    fulfillment: {
                                        title: 'Fulfillment Updates',
                                        icon: Schedule,
                                        fields: [
                                            { key: 'acai_request_id', label: 'ACAI Request ID' },
                                            { key: 'fulfillment_updates', label: 'Updates', type: 'fulfillment' }
                                        ]
                                    }
                                };

                                const fieldGroups = isCircuitOrder ? circuitOrderFieldGroups : standardFieldGroups;

                                return (
                                    <Box>
                                        {Object.entries(fieldGroups).map(([groupKey, group]) => {
                                            // Filter fields that have values
                                            const fieldsWithValues = group.fields.filter(field => {
                                                const keys = Array.isArray(field.key) ? field.key : [field.key];
                                                return keys.some(key => {
                                                    const value = selectedRequest[key];
                                                    if (value === null || value === undefined || value === '' || value === 'null' || value === '[]') return false;
                                                    // Circuit orders return already-parsed objects/arrays (quotes, updates) —
                                                    // an empty one means "nothing received yet", not a value to show.
                                                    if (Array.isArray(value)) return value.length > 0;
                                                    if (typeof value === 'object') return Object.keys(value).length > 0;
                                                    return true;
                                                });
                                            });

                                            if (fieldsWithValues.length === 0) return null;

                                            const GroupIcon = group.icon;

                                            return (
                                                <Box
                                                    key={groupKey}
                                                    sx={{
                                                        mb: 2.5,
                                                        bgcolor: 'white',
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        border: '1px solid #e2e8f0',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                                            borderColor: '#cbd5e1'
                                                        }
                                                    }}
                                                >
                                                    {/* Section Header */}
                                                    <Box
                                                        sx={{
                                                            px: 2.5,
                                                            py: 1.5,
                                                            bgcolor: '#f8fafc',
                                                            borderBottom: '1px solid #e2e8f0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: 1.5,
                                                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                boxShadow: '0 2px 6px rgba(59, 130, 246, 0.2)'
                                                            }}
                                                        >
                                                            <GroupIcon sx={{ color: 'white', fontSize: 16 }} />
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '0.9rem',
                                                                color: '#1e293b',
                                                                letterSpacing: '-0.01em'
                                                            }}
                                                        >
                                                            {group.title}
                                                        </Typography>
                                                    </Box>

                                                    {/* Section Content */}
                                                    <Box sx={{ p: 2.5 }}>
                                                        {groupKey === 'detailedInfo' ? (
                                                            <Box>
                                                                {(() => {
                                                                    const jsonField = fieldsWithValues.find(f => {
                                                                        const keys = Array.isArray(f.key) ? f.key : [f.key];
                                                                        return keys.includes('request_details_json');
                                                                    });

                                                                    if (!jsonField) return null;

                                                                    const value = selectedRequest['request_details_json'];
                                                                    if (!value) return null;

                                                                    let parsedData;
                                                                    try {
                                                                        parsedData = typeof value === 'string' ? JSON.parse(value) : value;
                                                                    } catch (e) {
                                                                        return (
                                                                            <Typography variant="body2" color="error">
                                                                                Failed to parse request details
                                                                            </Typography>
                                                                        );
                                                                    }

                                                                    // Render function for nested objects
                                                                    const renderSection = (title, data, depth = 0) => {
                                                                        if (!data || typeof data !== 'object') return null;

                                                                        const isArray = Array.isArray(data);

                                                                        return (
                                                                            <Box key={title} sx={{ mb: 2 }}>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: depth === 0 ? '#3b82f6' : '#64748b',
                                                                                        fontWeight: 700,
                                                                                        textTransform: 'uppercase',
                                                                                        fontSize: depth === 0 ? '0.7rem' : '0.65rem',
                                                                                        letterSpacing: '0.5px',
                                                                                        mb: 1,
                                                                                        display: 'block'
                                                                                    }}
                                                                                >
                                                                                    {title.replace(/_/g, ' ')}
                                                                                </Typography>
                                                                                {isArray ? (
                                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                                        {data.map((item, idx) => (
                                                                                            <Paper
                                                                                                key={idx}
                                                                                                elevation={0}
                                                                                                sx={{
                                                                                                    p: 1.5,
                                                                                                    bgcolor: '#f8fafc',
                                                                                                    border: '1px solid #e2e8f0',
                                                                                                    borderRadius: 1.5
                                                                                                }}
                                                                                            >
                                                                                                {typeof item === 'object' ? (
                                                                                                    <Grid container spacing={1.5}>
                                                                                                        {Object.entries(item).map(([key, val]) => (
                                                                                                            <Grid item xs={6} key={key}>
                                                                                                                <Typography
                                                                                                                    variant="caption"
                                                                                                                    sx={{
                                                                                                                        color: '#64748b',
                                                                                                                        fontWeight: 600,
                                                                                                                        fontSize: '0.65rem',
                                                                                                                        display: 'block',
                                                                                                                        mb: 0.25
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {key.replace(/_/g, ' ').toUpperCase()}
                                                                                                                </Typography>
                                                                                                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#1e293b' }}>
                                                                                                                    {typeof val === 'number' ? val.toFixed(2) : val?.toString() || 'N/A'}
                                                                                                                </Typography>
                                                                                                            </Grid>
                                                                                                        ))}
                                                                                                    </Grid>
                                                                                                ) : (
                                                                                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                                                                        {item}
                                                                                                    </Typography>
                                                                                                )}
                                                                                            </Paper>
                                                                                        ))}
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Grid container spacing={1.5}>
                                                                                        {Object.entries(data).map(([key, val]) => {
                                                                                            if (val === null || val === undefined) return null;
                                                                                            if (key === 'country_uplift') return null;

                                                                                            if (typeof val === 'object' && !Array.isArray(val)) {
                                                                                                return (
                                                                                                    <Grid item xs={12} key={key}>
                                                                                                        {renderSection(key, val, depth + 1)}
                                                                                                    </Grid>
                                                                                                );
                                                                                            }

                                                                                            if (Array.isArray(val)) {
                                                                                                return (
                                                                                                    <Grid item xs={12} key={key}>
                                                                                                        {renderSection(key, val, depth + 1)}
                                                                                                    </Grid>
                                                                                                );
                                                                                            }

                                                                                            return (
                                                                                                <Grid item xs={6} key={key}>
                                                                                                    <Typography
                                                                                                        variant="caption"
                                                                                                        sx={{
                                                                                                            color: '#64748b',
                                                                                                            fontWeight: 600,
                                                                                                            fontSize: '0.65rem',
                                                                                                            textTransform: 'uppercase',
                                                                                                            letterSpacing: '0.3px',
                                                                                                            display: 'block',
                                                                                                            mb: 0.25
                                                                                                        }}
                                                                                                    >
                                                                                                        {key.replace(/_/g, ' ')}
                                                                                                    </Typography>
                                                                                                    <Typography
                                                                                                        variant="body2"
                                                                                                        sx={{
                                                                                                            fontSize: '0.85rem',
                                                                                                            color: '#1e293b',
                                                                                                            fontWeight: 500
                                                                                                        }}
                                                                                                    >
                                                                                                        {typeof val === 'number' ? val.toFixed(2) : val?.toString() || 'N/A'}
                                                                                                    </Typography>
                                                                                                </Grid>
                                                                                            );
                                                                                        })}
                                                                                    </Grid>
                                                                                )}
                                                                            </Box>
                                                                        );
                                                                    };

                                                                    return (
                                                                        <Box>
                                                                            {Object.entries(parsedData).map(([section, data]) =>
                                                                                renderSection(section, data)
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })()}
                                                            </Box>
                                                        ) : groupKey === 'quotes' ? (
                                                            <Box>
                                                                {(() => {
                                                                    const comparison = selectedRequest.quote_comparison || {};
                                                                    const vendorRows = comparison.vendor_rows || [];
                                                                    const vendorQuotes = selectedRequest.vendor_quotes || {};
                                                                    const quoteEntries = Object.entries(vendorQuotes);
                                                                    const benchmark =
                                                                        selectedRequest.benchmark_quote && Object.keys(selectedRequest.benchmark_quote).length
                                                                            ? selectedRequest.benchmark_quote
                                                                            : comparison.benchmark || null;
                                                                    const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

                                                                    const breakdownRows = [
                                                                        ['SD-WAN Circuit(s) (908138)', 'sdWanCircuits'],
                                                                        ['SD-WAN Management (908134)', 'sdWanManagement'],
                                                                        ['SD-WAN Router (908137)', 'sdWanRouter'],
                                                                        ['SD-WAN IMACD — OTC Provider (908374)', 'sdWanImacd'],
                                                                        ['Integrator Effort — Accenture (905513)', 'accentureEffort'],
                                                                        ['SD-WAN Effort (2 PD) (908374)', 'sdWanEffort'],
                                                                        ['ODM Team Effort (905513)', 'odmTeamEffort']
                                                                    ];

                                                                    return (
                                                                        <>
                                                                            {/* Quotes received — vendor slots plus the benchmark */}
                                                                            {(quoteEntries.length > 0 || benchmark) && (
                                                                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                                                                    <Table size="small">
                                                                                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                                                                            <TableRow>
                                                                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Source</TableCell>
                                                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>MRC</TableCell>
                                                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>NRC</TableCell>
                                                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Quote Total</TableCell>
                                                                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Expiry</TableCell>
                                                                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Lead Time</TableCell>
                                                                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Quote File</TableCell>
                                                                                            </TableRow>
                                                                                        </TableHead>
                                                                                        <TableBody>
                                                                                            {quoteEntries.map(([slot, q]) => (
                                                                                                <TableRow
                                                                                                    key={slot}
                                                                                                    sx={{ bgcolor: slot === comparison.recommended_vendor ? '#ecfdf5' : 'inherit' }}
                                                                                                >
                                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                                                        {slot.replace(/_/g, ' ')}
                                                                                                        {slot === comparison.recommended_vendor && (
                                                                                                            <Chip label="Lowest" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                                                                                                        )}
                                                                                                    </TableCell>
                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{eur(q?.mrc)}</TableCell>
                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{eur(q?.nrc)}</TableCell>
                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{eur(q?.total)}</TableCell>
                                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{q?.expiry || '—'}</TableCell>
                                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{q?.leadtime || '—'}</TableCell>
                                                                                                    <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{q?.filename || '—'}</TableCell>
                                                                                                </TableRow>
                                                                                            ))}
                                                                                            {benchmark && (
                                                                                                <TableRow sx={{ bgcolor: '#fffbeb' }}>
                                                                                                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Benchmark</TableCell>
                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{eur(benchmark.mrc)}</TableCell>
                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{eur(benchmark.nrc)}</TableCell>
                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{eur(benchmark.total)}</TableCell>
                                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{benchmark.expiry || '—'}</TableCell>
                                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{benchmark.leadtime || '—'}</TableCell>
                                                                                                    <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{benchmark.filename || '—'}</TableCell>
                                                                                                </TableRow>
                                                                                            )}
                                                                                        </TableBody>
                                                                                    </Table>
                                                                                </TableContainer>
                                                                            )}

                                                                            {/* Comparison outcome */}
                                                                            {comparison.recommended_vendor && (
                                                                                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                                                                    <Chip
                                                                                        label={`Recommended: ${String(comparison.recommended_vendor).replace(/_/g, ' ')}`}
                                                                                        size="small"
                                                                                        sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, height: 24 }}
                                                                                    />
                                                                                    {comparison.winning_total !== null && comparison.winning_total !== undefined && (
                                                                                        <Chip
                                                                                            label={`Winning Quote: ${eur(comparison.winning_total)}`}
                                                                                            size="small"
                                                                                            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, height: 24 }}
                                                                                        />
                                                                                    )}
                                                                                    {comparison.within_benchmark !== null && comparison.within_benchmark !== undefined && (
                                                                                        <Chip
                                                                                            label={comparison.within_benchmark ? 'Within Benchmark' : 'Above Benchmark'}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                bgcolor: comparison.within_benchmark ? '#dcfce7' : '#fef2f2',
                                                                                                color: comparison.within_benchmark ? '#166534' : '#b91c1c',
                                                                                                fontWeight: 600,
                                                                                                height: 24
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    {comparison.requires_strategic_approval && (
                                                                                        <Chip
                                                                                            label="Strategic Approval Required (> €250,000)"
                                                                                            size="small"
                                                                                            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, height: 24 }}
                                                                                        />
                                                                                    )}
                                                                                </Stack>
                                                                            )}

                                                                            {/* Full cost estimate per vendor — same components as the Review Quote popup */}
                                                                            {vendorRows.length > 0 && (
                                                                                <>
                                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1e293b' }}>
                                                                                        Full Cost Estimate by Vendor
                                                                                    </Typography>
                                                                                    {vendorRows.map((row) => (
                                                                                        <Accordion
                                                                                            key={row.vendor}
                                                                                            disableGutters
                                                                                            elevation={0}
                                                                                            sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}
                                                                                        >
                                                                                            <AccordionSummary
                                                                                                expandIcon={<ExpandMoreIcon />}
                                                                                                sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0, alignItems: 'center', gap: 1 } }}
                                                                                            >
                                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                                    {String(row.vendor || '').replace(/_/g, ' ')}
                                                                                                </Typography>
                                                                                                {row.vendor === comparison.recommended_vendor && (
                                                                                                    <Chip label="Recommended" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />
                                                                                                )}
                                                                                                <Typography variant="body2" sx={{ ml: 'auto', mr: 1, fontWeight: 700 }}>
                                                                                                    Total: {eur(row.total_cost)}
                                                                                                </Typography>
                                                                                            </AccordionSummary>
                                                                                            <AccordionDetails sx={{ p: 1, bgcolor: '#f8fafc' }}>
                                                                                                <TableContainer>
                                                                                                    <Table size="small">
                                                                                                        <TableBody>
                                                                                                            {breakdownRows.map(([label, key]) => (
                                                                                                                <TableRow key={key}>
                                                                                                                    <TableCell sx={{ fontSize: '0.8rem', border: 0, py: 0.5 }}>{label}</TableCell>
                                                                                                                    <TableCell align="right" sx={{ fontSize: '0.8rem', border: 0, py: 0.5 }}>
                                                                                                                        {eur(row.cost_breakdown?.[key])}
                                                                                                                    </TableCell>
                                                                                                                </TableRow>
                                                                                                            ))}
                                                                                                        </TableBody>
                                                                                                    </Table>
                                                                                                </TableContainer>
                                                                                                <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                                                                    Numbers in brackets are material numbers. Vendor quote — MRC: {eur(row.mrc)} | NRC: {eur(row.nrc)}
                                                                                                </Typography>
                                                                                            </AccordionDetails>
                                                                                        </Accordion>
                                                                                    ))}
                                                                                </>
                                                                            )}

                                                                            {quoteEntries.length === 0 && vendorRows.length === 0 && !benchmark && (
                                                                                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                                                                                    No quotes received yet.
                                                                                </Typography>
                                                                            )}
                                                                        </>
                                                                    );
                                                                })()}
                                                            </Box>
                                                        ) : groupKey === 'fulfillment' && fieldsWithValues.find(f => (Array.isArray(f.key) ? f.key : [f.key]).some(k => k === 'fulfillmentUpdates' || k === 'fulfillment_updates')) ? (
                                                            <Box>
                                                                {fieldsWithValues.map((field, idx) => {
                                                                    const keys = Array.isArray(field.key) ? field.key : [field.key];
                                                                    const key = keys.find(k => selectedRequest[k] !== undefined && selectedRequest[k] !== null);
                                                                    if (!key) return null;
                                                                    const value = selectedRequest[key];

                                                                    if (key === 'fulfillmentUpdates' || key === 'fulfillment_updates') {
                                                                        // Parse if it's a JSON string
                                                                        let updates = value;
                                                                        if (typeof value === 'string') {
                                                                            try {
                                                                                updates = JSON.parse(value);
                                                                            } catch (e) {
                                                                                return null;
                                                                            }
                                                                        }

                                                                        if (!Array.isArray(updates) || updates.length === 0) {
                                                                            return null;
                                                                        }

                                                                        // Status detection helper
                                                                        const getUpdateStatus = (comment) => {
                                                                            const lowerComment = comment.toLowerCase();
                                                                            if (lowerComment.includes('completed') || lowerComment.includes('done') ||
                                                                                lowerComment.includes('finished') || lowerComment.includes('resolved')) {
                                                                                return 'completed';
                                                                            }
                                                                            if (lowerComment.includes('in progress') || lowerComment.includes('working') ||
                                                                                lowerComment.includes('started') || lowerComment.includes('fulfillment started')) {
                                                                                return 'in_progress';
                                                                            }
                                                                            if (lowerComment.includes('submitted') || lowerComment.includes('created') ||
                                                                                lowerComment.includes('acai request id')) {
                                                                                return 'submitted';
                                                                            }
                                                                            return 'info';
                                                                        };

                                                                        const statusConfig = {
                                                                            completed: { icon: TaskAlt, color: '#10b981', bgColor: '#ecfdf5', label: 'Completed' },
                                                                            in_progress: { icon: PlayCircle, color: '#3b82f6', bgColor: '#eff6ff', label: 'In Progress' },
                                                                            submitted: { icon: CheckCircle, color: '#f59e0b', bgColor: '#fffbeb', label: 'Submitted' },
                                                                            info: { icon: Info, color: '#6366f1', bgColor: '#eef2ff', label: 'Info' }
                                                                        };

                                                                        return (
                                                                            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                                                                {updates.map((update, updateIdx) => {
                                                                                    const status = getUpdateStatus(update.comment);
                                                                                    const config = statusConfig[status];
                                                                                    const StatusIcon = config.icon;

                                                                                    return (
                                                                                        <Paper
                                                                                            key={updateIdx}
                                                                                            elevation={0}
                                                                                            sx={{
                                                                                                p: 1.5,
                                                                                                background: config.bgColor,
                                                                                                border: `1px solid ${config.color}30`,
                                                                                                borderRadius: 1.5,
                                                                                                position: 'relative',
                                                                                                transition: 'all 0.2s ease',
                                                                                                '&:hover': {
                                                                                                    boxShadow: `0 2px 8px ${config.color}15`,
                                                                                                    borderColor: `${config.color}50`
                                                                                                },
                                                                                                '&::before': {
                                                                                                    content: '""',
                                                                                                    position: 'absolute',
                                                                                                    left: 0,
                                                                                                    top: 0,
                                                                                                    bottom: 0,
                                                                                                    width: 3,
                                                                                                    bgcolor: config.color,
                                                                                                    borderRadius: '1.5px 0 0 1.5px'
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        width: 24,
                                                                                                        height: 24,
                                                                                                        borderRadius: 1,
                                                                                                        bgcolor: config.color,
                                                                                                        display: 'flex',
                                                                                                        alignItems: 'center',
                                                                                                        justifyContent: 'center'
                                                                                                    }}
                                                                                                >
                                                                                                    <StatusIcon sx={{ fontSize: 14, color: 'white' }} />
                                                                                                </Box>
                                                                                                <Chip
                                                                                                    label={config.label}
                                                                                                    size="small"
                                                                                                    sx={{
                                                                                                        height: 20,
                                                                                                        fontSize: '0.65rem',
                                                                                                        fontWeight: 600,
                                                                                                        bgcolor: 'white',
                                                                                                        color: config.color,
                                                                                                        border: `1px solid ${config.color}40`
                                                                                                    }}
                                                                                                />
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    sx={{
                                                                                                        ml: 'auto',
                                                                                                        color: '#64748b',
                                                                                                        fontWeight: 600,
                                                                                                        display: 'flex',
                                                                                                        alignItems: 'center',
                                                                                                        gap: 0.3,
                                                                                                        fontSize: '0.65rem'
                                                                                                    }}
                                                                                                >
                                                                                                    <Schedule sx={{ fontSize: 11 }} />
                                                                                                    {new Date(update.timestamp).toLocaleString('en-US', {
                                                                                                        month: 'short',
                                                                                                        day: 'numeric',
                                                                                                        hour: '2-digit',
                                                                                                        minute: '2-digit'
                                                                                                    })}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                            <Typography
                                                                                                variant="body2"
                                                                                                sx={{
                                                                                                    color: '#1e293b',
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.85rem',
                                                                                                    lineHeight: 1.5,
                                                                                                    pl: 4
                                                                                                }}
                                                                                            >
                                                                                                {update.comment}
                                                                                            </Typography>
                                                                                        </Paper>
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                        );
                                                                    }

                                                                    if (key === 'acaiRequestId' || key === 'acai_request_id') {
                                                                        return (
                                                                            <Box key={idx} mb={1.5}>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: '#64748b',
                                                                                        fontWeight: 600,
                                                                                        textTransform: 'uppercase',
                                                                                        fontSize: '0.65rem',
                                                                                        letterSpacing: '0.5px',
                                                                                        mb: 0.5,
                                                                                        display: 'block'
                                                                                    }}
                                                                                >
                                                                                    {field.label}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={value}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        bgcolor: '#eff6ff',
                                                                                        color: '#3b82f6',
                                                                                        fontWeight: 600,
                                                                                        fontSize: '0.75rem',
                                                                                        border: '1px solid #3b82f640',
                                                                                        height: 26
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        );
                                                                    }

                                                                    return null;
                                                                })}
                                                            </Box>
                                                        ) : (
                                                            <Grid container spacing={2.5}>
                                                                {fieldsWithValues.map((field, idx) => {
                                                                    const keys = Array.isArray(field.key) ? field.key : [field.key];
                                                                    const key = keys.find(k => selectedRequest[k] !== undefined && selectedRequest[k] !== null);
                                                                    if (!key) return null;
                                                                    let value = selectedRequest[key];

                                                                    // Skip empty values
                                                                    if (value === '' || value === 'null' || value === '[]') return null;

                                                                    // Handle boolean values
                                                                    if (field.type === 'boolean') {
                                                                        value = value ? 'Yes' : 'No';
                                                                    }

                                                                    // Handle material number with special rendering
                                                                    if (field.type === 'material' && selectedRequest.materialNumber) {
                                                                        return (
                                                                            <Grid item xs={12} key={idx}>
                                                                                <Box>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: '#64748b',
                                                                                            fontWeight: 600,
                                                                                            textTransform: 'uppercase',
                                                                                            fontSize: '0.65rem',
                                                                                            letterSpacing: '0.5px',
                                                                                            mb: 0.5,
                                                                                            display: 'block'
                                                                                        }}
                                                                                    >
                                                                                        {field.label}
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                                        <Chip
                                                                                            label={selectedRequest.materialNumber}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                bgcolor: '#dcfce7',
                                                                                                color: '#166534',
                                                                                                fontWeight: 700,
                                                                                                fontSize: '0.8rem',
                                                                                                height: 28,
                                                                                                border: '1px solid #86efac',
                                                                                                fontFamily: 'monospace'
                                                                                            }}
                                                                                        />
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                fontSize: '0.85rem',
                                                                                                color: '#64748b',
                                                                                                fontStyle: 'italic'
                                                                                            }}
                                                                                        >
                                                                                            {selectedRequest.materialNumberDescription}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Grid>
                                                                        );
                                                                    }

                                                                    const isLongText = field.type === 'long';

                                                                    return (
                                                                        <Grid item xs={12} sm={isLongText ? 12 : 6} key={idx}>
                                                                            <Box>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: '#64748b',
                                                                                        fontWeight: 600,
                                                                                        textTransform: 'uppercase',
                                                                                        fontSize: '0.65rem',
                                                                                        letterSpacing: '0.5px',
                                                                                        mb: 0.5,
                                                                                        display: 'block'
                                                                                    }}
                                                                                >
                                                                                    {field.label}
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        color: '#1e293b',
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                        lineHeight: 1.5,
                                                                                        whiteSpace: isLongText ? 'pre-wrap' : 'normal'
                                                                                    }}
                                                                                >
                                                                                    {value || 'N/A'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Grid>
                                                                    );
                                                                })}
                                                            </Grid>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })()}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 2.5,
                        py: 2,
                        borderTop: '1px solid #e2e8f0',
                        background: '#f8fafc'
                    }}
                >
                    <Button
                        onClick={() => setViewDialogOpen(false)}
                        variant="contained"
                        sx={{
                            px: 3.5,
                            py: 1,
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                            boxShadow: '0 3px 8px rgba(30, 41, 59, 0.15)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Existing Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you wish to delete this request?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error">
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Existing Delete Result Dialog */}
            <Dialog
                open={deleteResultDialogOpen}
                onClose={() => setDeleteResultDialogOpen(false)}
                aria-labelledby="delete-result-dialog-title"
            >
                <DialogTitle id="delete-result-dialog-title">
                    {deleteSuccess ? 'Success' : 'Cannot Delete'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteResultMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteResultDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Existing Fulfillment Updates Dialog */}
            <FulfillmentUpdatesDialog
                open={fulfillmentDialogOpen}
                onClose={() => setFulfillmentDialogOpen(false)}
                requestId={selectedFulfillmentRequestId}
                updates={currentFulfillmentUpdates}
                onUpdateAdded={handleFulfillmentUpdateAdded}
            />

            {/* New Quote Review Dialog */}
            <Dialog
                open={quoteReviewDialogOpen}
                onClose={() => setQuoteReviewDialogOpen(false)}
                maxWidth="md"
                fullWidth
                aria-labelledby="quote-review-dialog-title"
            >
                <DialogTitle id="quote-review-dialog-title">
                    <Typography variant="h6">Quote Review</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {!quoteData ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : quoteData.isCircuitOrder ? (
                        /* ── Circuit Order Quote Comparison + Cost Breakdown ── */
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                                Quote Comparison — {selectedQuoteRequest?.odm_title}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>GRM: {selectedQuoteRequest?.grm_demand_number}</Typography>

                            {/* Alerts */}
                            {quoteData.circuitComparison?.within_benchmark === false && (
                                <Alert severity="warning" sx={{ mb: 1 }}>Recommended quote exceeds benchmark price.</Alert>
                            )}
                            {quoteData.circuitComparison?.requires_strategic_approval && (
                                <Alert severity="warning" sx={{ mb: 1 }}>Total &gt; €250,000 — strategic approval required.</Alert>
                            )}

                            {/* Vendor comparison table */}
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#003781' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Vendor</TableCell>
                                            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>MRC (€)</TableCell>
                                            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>NRC (€)</TableCell>
                                            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>Quote Total (€)</TableCell>
                                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Expiry</TableCell>
                                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Lead Time</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(quoteData.circuitComparison?.vendor_rows || []).map((row) => (
                                            <TableRow key={row.vendor}
                                                sx={{ bgcolor: row.vendor === quoteData.circuitComparison?.recommended_vendor ? '#e8f5e9' : 'inherit' }}>
                                                <TableCell>
                                                    {row.vendor}
                                                    {row.vendor === quoteData.circuitComparison?.recommended_vendor &&
                                                        <Chip label="Recommended" size="small" color="success" sx={{ ml: 1 }} />}
                                                </TableCell>
                                                <TableCell align="right">€{(row.mrc || 0).toFixed(2)}</TableCell>
                                                <TableCell align="right">€{(row.nrc || 0).toFixed(2)}</TableCell>
                                                <TableCell align="right">€{(row.total || 0).toFixed(2)}</TableCell>
                                                <TableCell>{row.expiry}</TableCell>
                                                <TableCell>{row.leadtime}</TableCell>
                                            </TableRow>
                                        ))}
                                        {quoteData.circuitComparison?.benchmark && (
                                            <TableRow sx={{ bgcolor: '#fff3e0' }}>
                                                <TableCell><strong>Benchmark</strong></TableCell>
                                                <TableCell align="right">€{(quoteData.circuitComparison.benchmark.mrc || 0).toFixed(2)}</TableCell>
                                                <TableCell align="right">€{(quoteData.circuitComparison.benchmark.nrc || 0).toFixed(2)}</TableCell>
                                                <TableCell align="right">€{(quoteData.circuitComparison.benchmark.total || 0).toFixed(2)}</TableCell>
                                                <TableCell colSpan={2} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Full cost breakdown per vendor — same as bandwidth upgrade popup */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Full Cost Estimate by Vendor</Typography>
                            {(quoteData.circuitComparison?.vendor_rows || []).map((row) => (
                                <Box key={row.vendor} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{row.vendor}</Typography>
                                        {row.vendor === quoteData.circuitComparison?.recommended_vendor &&
                                            <Chip label="Recommended" size="small" color="success" />}
                                        <Typography variant="subtitle2" sx={{ ml: 'auto', fontWeight: 700 }}>
                                            Total: €{(row.total_cost || 0).toFixed(2)}
                                        </Typography>
                                    </Box>
                                    <Accordion disableGutters elevation={0} sx={{ border: '1px solid #f0f0f0' }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                                            sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0 } }}>
                                            <Typography variant="body2">View Cost Breakdown</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 1 }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Circuit(s) (908138)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.sdWanCircuits || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Management (908134)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.sdWanManagement || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Router (908137)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.sdWanRouter || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN IMACD — OTC Provider (908374)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.sdWanImacd || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>Integrator Effort — Accenture (905513)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.accentureEffort || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Effort (2 PD) (908374)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.sdWanEffort || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>ODM Team Effort (905513)</TableCell>
                                                            <TableCell align="right">€{(row.cost_breakdown?.odmTeamEffort || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                Numbers in brackets are material numbers. MRC: €{(row.mrc || 0).toFixed(2)} | NRC: €{(row.nrc || 0).toFixed(2)}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            ))}

                            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
                                Would you like to approve this quote and proceed with implementation?
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ mb: 2 }}>
                            {/* Request Summary */}
                            <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                                Request Summary
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">ODM Title</Typography>
                                    <Typography>{selectedQuoteRequest?.odm_title}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">Request Type</Typography>
                                    <Typography>{selectedQuoteRequest?.request_type}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">Site ID</Typography>
                                    <Typography>{selectedQuoteRequest?.site_id}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">GRM Number</Typography>
                                    <Typography>{selectedQuoteRequest?.grm_demand_number}</Typography>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 3, mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                                    Final Quote Details
                                </Typography>
                                <Typography variant="h5" gutterBottom sx={{ color: '#003781', fontWeight: 'bold' }}>
                                    Total Cost: €{quoteData.total?.toFixed(2) || "0.00"}
                                </Typography>

                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography sx={{ fontWeight: 500 }}>Cost Breakdown</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            SD-WAN Circuit(s) (908138)
                                                            <InfoTooltip description={costComponentDescriptions.sdWanCircuits} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.sdWanCircuits?.toFixed(2) || "0.00"}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            SD-WAN Management (908134)
                                                            <InfoTooltip description={costComponentDescriptions.sdWanManagement} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.sdWanManagement?.toFixed(2) || "0.00"}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            SD-WAN Router S/M/L/XL (908137)
                                                            <InfoTooltip description={costComponentDescriptions.sdWanRouter} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.sdWanRouter?.toFixed(2) || "0.00"}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            SD-WAN IMACD (OTC Provider) (908374)
                                                            <InfoTooltip description={costComponentDescriptions.sdWanImacd} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.sdWanImacd?.toFixed(2) || "0.00"}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            Integrator Effort (Accenture) (905513)
                                                            <InfoTooltip description={costComponentDescriptions.accentureEffort} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.accentureEffort?.toFixed(2) || "0.00"}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            SDWAN effort for bandwidth upgrade (2 PD) (908374)
                                                            <InfoTooltip description={costComponentDescriptions.sdWanEffort} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.sdWanEffort?.toFixed(2) || "348.14"}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row">
                                                            ODM team effort (905513)
                                                            <InfoTooltip description={costComponentDescriptions.odmTeamEffort} />
                                                        </TableCell>
                                                        <TableCell align="right">€{quoteData.breakdown?.odmTeamEffort?.toFixed(2) || "924.00"}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', fontStyle: 'italic' }}>
                                            Note: Numbers in brackets denote material numbers.
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>

                                {quoteData.details?.circuitDetails && quoteData.details.circuitDetails.length > 0 && (
                                    <Accordion sx={{ mt: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography>Circuit Details</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Circuit ID</TableCell>
                                                            <TableCell>Router Model</TableCell>
                                                            <TableCell>Option Used</TableCell>
                                                            <TableCell align="right">MRC (€)</TableCell>
                                                            <TableCell align="right">NRC (€)</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {quoteData.details.circuitDetails.map((circuit, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>{circuit.circuit_id}</TableCell>
                                                                <TableCell>{circuit.router_model || 'Standard'}</TableCell>
                                                                <TableCell>{circuit.option_used || 'Default'}</TableCell>
                                                                <TableCell align="right">{circuit.mrc?.toFixed(2) || "0.00"}</TableCell>
                                                                <TableCell align="right">{circuit.nrc?.toFixed(2) || "0.00"}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>

                            <Typography variant="body1" sx={{ mt: 4, mb: 1, fontWeight: 500 }}>
                                Would you like to approve this quote and proceed with implementation?
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button
                        onClick={handleRejectQuote}
                        color="secondary"
                        variant="outlined"
                        disabled={isApproving || isRejecting}
                    >
                        {isRejecting ? "Rejecting..." : "Reject Quote"}
                    </Button>
                    <Button
                        onClick={handleApproveQuote}
                        color="primary"
                        variant="contained"
                        disabled={isApproving || isRejecting}
                    >
                        {isApproving ? "Approving..." : "Approve Quote"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Result Dialog after approve/reject */}
            <Dialog
                open={resultDialogOpen}
                onClose={() => setResultDialogOpen(false)}
                aria-labelledby="result-dialog-title"
            >
                <DialogTitle id="result-dialog-title">
                    {resultSuccess ? 'Success' : 'Information'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {resultMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResultDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default TrackRequests;
